// Live's Configuration of its 19 Pixi filters (#265 part 2, spec docs/specs/editor/effects.md):
// each filter's controls in live's order, with live's defaults, ranges and steps. A clip keeps only
// what was changed (`values`, by the parameter's name); the rest is the default. Read by the
// Parameters panel, which draws the controls, and by the engine, which makes the filter.
import type { EffectKind } from "../../api/generated/model";

/** An X/Y pair of live's Configuration. */
export interface Point {
  x: number;
  y: number;
}

export type EffectValue = number | boolean | string | Point;

/** One of live's controls: its parameter, what it is, and its default. */
export type FilterParam =
  /** A slider with its number box. */
  | { name: string; kind: "range"; min: number; max: number; step: number; value: number }
  /** Two rows, X and Y, each a slider with its number box. */
  | { name: string; kind: "point"; min: number; max: number; step: number; value: Point }
  /** A colour field, `#RRGGBB`. */
  | { name: string; kind: "color"; value: string }
  /** A checkbox. */
  | { name: string; kind: "flag"; value: boolean }
  /** A select of named numbers ("Select type"). */
  | { name: string; kind: "choice"; options: readonly { value: number; label: string }[]; value: number };

/** The kinds drawn by `pixi-filters`. */
export type FilterKind = Extract<
  EffectKind,
  | "adjustment-filter"
  | "advanced-bloom-filter"
  | "backdrop-blur-filter"
  | "bloom-filter"
  | "color-map-filter"
  | "convolution-filter"
  | "dot-filter"
  | "glitch-filter"
  | "glow-filter"
  | "godray-filter"
  | "grayscale-filter"
  | "hsl-adjustment-filter"
  | "kawase-blur-filter"
  | "motion-blur-filter"
  | "old-film-filter"
  | "radial-blur-filter"
  | "reflection-filter"
  | "rgb-split-filter"
  | "tilt-shift-filter"
>;

const range = (name: string, value: number, min: number, max: number, step: number): FilterParam => ({
  name,
  kind: "range",
  min,
  max,
  step,
  value,
});
const point = (name: string, x: number, y: number, min: number, max: number, step: number): FilterParam => ({
  name,
  kind: "point",
  min,
  max,
  step,
  value: { x, y },
});
const unit = (name: string, value: number) => range(name, value, 0, 1, 0.01);
const level = (name: string, value: number) => range(name, value, 0, 5, 0.07);

/** Live's controls of each filter, in live's order; none for Grayscale Filter. */
export const FILTER_PARAMS: Record<FilterKind, readonly FilterParam[]> = {
  "adjustment-filter": [
    level("gamma", 1.5),
    level("saturation", 0.5),
    level("contrast", 1.2),
    level("brightness", 1.5),
    level("red", 1.5),
    level("green", 1.5),
    level("blue", 1.5),
    level("alpha", 1),
  ],
  "advanced-bloom-filter": [
    range("threshold", 0.2, 0.1, 0.9, 0.009),
    range("bloomScale", 1, 0.5, 1.5, 0.015),
    range("brightness", 1, 0.5, 1.5, 0.015),
    range("blur", 20, 0, 20, 0.2),
    range("quality", 1, 1, 20, 1),
  ],
  "backdrop-blur-filter": [range("blur", 10, 0, 100, 2.3), range("quality", 4, 1, 10, 0.1)],
  "bloom-filter": [
    range("kernelSize", 5, 0, 15, 1),
    range("quality", 2, 0, 10, 1),
    range("resolution", 3, 0, 10, 1),
    point("strength", 10, 10, 0, 20, 0.5),
  ],
  "color-map-filter": [range("mix", 1, 0, 1, 0.013), { name: "nearest", kind: "flag", value: true }],
  "convolution-filter": [range("width", 3, 0, 500, 1), range("height", 3, 0, 500, 1)],
  "dot-filter": [range("scale", 1, 0.3, 1, 0.01), range("angle", 1, 0, 5, 0.05), { name: "grayscale", kind: "flag", value: true }],
  "glitch-filter": [
    unit("seed", 0.5),
    range("slices", 10, 1, 50, 1),
    unit("offset", 0.1),
    range("direction", 0, -180, 180, 1),
    point("red", 2, 2, -20, 20, 0.2),
    point("green", 10, -4, -20, 20, 0.2),
    point("blue", -10, 4, -20, 20, 0.2),
    {
      name: "fillMode",
      kind: "choice",
      options: [
        { value: 0, label: "TRANSPARENT" },
        { value: 1, label: "ORIGINAL" },
        { value: 2, label: "LOOP" },
        { value: 3, label: "CLAMP" },
        { value: 4, label: "MIRROR" },
      ],
      value: 0,
    },
  ],
  "glow-filter": [
    range("distance", 10, 0, 50, 0.5),
    range("outerStrength", 2, 0, 20, 0.2),
    range("innerStrength", 0, 0, 20, 0.2),
    range("quality", 1, 1, 10, 1),
    unit("alpha", 1),
    { name: "color", kind: "color", value: "#FFFFFF" }, // raw-color: live's Glow colour, a value
    { name: "knockout", kind: "flag", value: false },
  ],
  "godray-filter": [
    range("time", 0, 0, 100, 0.1),
    unit("gain", 0.6),
    range("lacunarity", 2.75, 0, 5, 0.05),
    unit("alpha", 1),
    range("angle", 30, -180, 180, 1),
    point("center", 426, -100, -1000, 1000, 10),
    { name: "parallel", kind: "flag", value: true },
  ],
  "grayscale-filter": [],
  "hsl-adjustment-filter": [
    range("hue", 100, -180, 180, 1),
    range("saturation", 1, -1, 1, 0.01),
    range("lightness", 0, -1, 1, 0.01),
    unit("alpha", 1),
    { name: "colorize", kind: "flag", value: false },
  ],
  "kawase-blur-filter": [range("strength", 4, 0, 50, 0.5), range("quality", 3, 1, 10, 1), point("pixelSize", 1, 1, 0, 20, 0.2)],
  "motion-blur-filter": [point("velocity", 40, 40, -100, 100, 1), range("kernelSize", 5, 1, 21, 2), range("offset", 0, -100, 100, 1)],
  "old-film-filter": [
    unit("sepia", 0.3),
    unit("noise", 0.3),
    range("noiseSize", 1, 1, 10, 0.1),
    unit("scratch", 0.5),
    unit("scratchDensity", 0.5),
    range("scratchWidth", 1, 1, 10, 0.1),
    unit("vignetting", 0.3),
    unit("vignettingAlpha", 1),
    unit("vignettingBlur", 0.3),
  ],
  "radial-blur-filter": [
    range("angle", 20, -180, 180, 1),
    point("center", 0, 0, -1000, 1000, 10),
    range("radius", 300, 0, 1000, 10),
    range("kernelSize", 15, 1, 50, 2),
  ],
  "reflection-filter": [unit("boundary", 0.5)],
  "rgb-split-filter": [point("red", 2, 2, -20, 20, 0.2), point("green", 10, -4, -20, 20, 0.2), point("blue", -10, 4, -20, 20, 0.2)],
  "tilt-shift-filter": [
    range("blur", 100, 0, 500, 5),
    range("gradientBlur", 600, 0, 1000, 10),
    point("start", 0, 0, -1000, 1000, 10),
    point("end", 853, 414, -1000, 1000, 10),
  ],
};

export function isFilter(kind: EffectKind): kind is FilterKind {
  return kind in FILTER_PARAMS;
}

/** Live's label of a parameter: its name split before each capital, each word capitalised. */
export function paramLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (first) => first.toUpperCase())
    .replace(/ ([a-z])/g, (_, letter: string) => ` ${letter.toUpperCase()}`);
}

const within = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/** A value as its control keeps it: in range, of its kind; the default for anything else. */
export function heldValue(param: FilterParam, value: unknown): EffectValue {
  switch (param.kind) {
    case "range":
      return typeof value === "number" && Number.isFinite(value) ? within(value, param.min, param.max) : param.value;
    case "point": {
      const given = value as Partial<Point> | null;
      if (typeof given !== "object" || given === null || typeof given.x !== "number" || typeof given.y !== "number") {
        return param.value;
      }
      return { x: within(given.x, param.min, param.max), y: within(given.y, param.min, param.max) };
    }
    case "color":
      return typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value) ? value.toUpperCase() : param.value;
    case "flag":
      return typeof value === "boolean" ? value : param.value;
    case "choice":
      return param.options.some((option) => option.value === value) ? (value as number) : param.value;
  }
}

/** A filter's every parameter: the clip's value where it set one, else live's default. */
export function filterValues(
  kind: FilterKind,
  saved: Readonly<Record<string, EffectValue>>,
): Readonly<Record<string, EffectValue>> {
  return Object.fromEntries(FILTER_PARAMS[kind].map((param) => [param.name, heldValue(param, saved[param.name])]));
}
